<script setup lang="ts">
  defineOptions({ name: 'EventDetailSpeakersSection' })

  import { computed, ref, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { FilterLinesIcon, SearchLgIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPCheckbox from '@ui/components/CPCheckbox.vue'
  import CPInput from '@ui/components/CPInput.vue'
  import CPPopover from '@ui/components/CPPopover.vue'
  import CPTag from '@ui/components/CPTag.vue'
  import CPText from '@ui/components/CPText.vue'

  import EventDetailSpeakerProfile from './EventDetailSpeakerProfile.vue'

  import type { EventDetailPerson } from '../types/event-detail'

  import type { EventsSpeakerDetailResource } from '../types/events-detail-experience-controller'

  const props = defineProps({
    speakers: {
      type: Array as PropType<readonly EventDetailPerson[]>,
      required: true,
    },
    speakerDetail: {
      type: Object as PropType<EventsSpeakerDetailResource>,
      default: () => ({
        state: 'empty' as const,
        speakerId: '',
        bio: '',
        links: [],
        sessions: [],
      }),
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    openSpeaker: [speakerId: string]
    closeSpeaker: []
    addToCalendar: [sessionId: string]
  }>()

  const { t } = useI18n()

  const searchQuery = ref('')
  const selectedLabels = ref<string[]>([])

  const uniqueLabels = computed(() => {
    const seen = new Set<string>()
    const labels: string[] = []
    for (const speaker of props.speakers) {
      const label = speaker.tag?.trim()
      if (!label || seen.has(label)) continue
      seen.add(label)
      labels.push(label)
    }
    return labels
  })

  const hasLabelFilters = computed(() => uniqueLabels.value.length > 0)

  const matchedSpeakers = computed(() => {
    const allowed = new Set(uniqueLabels.value)
    const labels = new Set(selectedLabels.value.filter(label => allowed.has(label)))
    const byLabel =
      labels.size === 0 ? [...props.speakers] : props.speakers.filter(speaker => Boolean(speaker.tag && labels.has(speaker.tag)))
    const query = searchQuery.value.trim().toLowerCase()
    if (!query) return byLabel
    return byLabel.filter(
      speaker =>
        speaker.name.toLowerCase().includes(query) ||
        speaker.role.toLowerCase().includes(query) ||
        (speaker.tag ?? '').toLowerCase().includes(query) ||
        (speaker.bio ?? '').toLowerCase().includes(query)
    )
  })

  const profileSpeaker = computed(() => {
    const speakerId = props.speakerDetail.speakerId?.trim() ?? ''
    if (!speakerId) return null
    return props.speakers.find(speaker => speaker.id === speakerId) ?? { id: speakerId, name: '', role: '' }
  })

  const openSpeaker = (speaker: EventDetailPerson) => {
    emit('openSpeaker', speaker.id)
  }

  const isLabelSelected = (label: string) => selectedLabels.value.includes(label)

  const setLabelSelected = (label: string, checked: boolean) => {
    if (checked) {
      if (!selectedLabels.value.includes(label)) selectedLabels.value = [...selectedLabels.value, label]
      return
    }
    selectedLabels.value = selectedLabels.value.filter(item => item !== label)
  }

  const clearLabelFilters = () => {
    selectedLabels.value = []
  }

  const labelFilterId = (label: string) =>
    createElementId(
      props.idPrefix,
      'speakers-filter',
      label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'label'
    )
</script>

<template>
  <EventDetailSpeakerProfile
    v-if="profileSpeaker"
    :speaker="profileSpeaker"
    :bio="speakerDetail.bio"
    :links="speakerDetail.links"
    :sessions="speakerDetail.sessions"
    :state="speakerDetail.state"
    :id-prefix="idPrefix"
    @close-speaker="emit('closeSpeaker')"
    @add-to-calendar="emit('addToCalendar', $event)"
  />
  <div
    v-else
    :id="createElementId(idPrefix, 'speakers', 'root')"
    class="mx-auto flex w-full max-w-[82.5rem] flex-col gap-5 px-4 py-6 md:px-6"
  >
    <div
      :id="createElementId(idPrefix, 'speakers', 'toolbar')"
      class="flex flex-col gap-3 md:flex-row md:items-center md:justify-end md:gap-4"
    >
      <div class="flex shrink-0 flex-nowrap items-center gap-2">
        <CPInput
          :id="createElementId(idPrefix, 'speakers', 'search')"
          :model-value="searchQuery"
          :prefix-icon="SearchLgIcon"
          type="text"
          size="sm"
          class="box-border w-[min(100%,16.25rem)] min-w-[12rem] sm:w-[16.25rem]"
          :placeholder="t('clientportal.events.detail.speakersSearch')"
          @update:model-value="searchQuery = String($event ?? '')"
        />
        <CPPopover
          v-if="hasLabelFilters"
          :id="createElementId(idPrefix, 'speakers', 'tag-filters')"
          trigger="click"
          placement="bottom-end"
          :show-arrow="false"
        >
          <template #trigger>
            <CPButton :id="createElementId(idPrefix, 'speakers', 'filters-trigger')" type="secondary" color="secondary" size="sm">
              <template #iconLeft>
                <FilterLinesIcon class="h-4 w-4" aria-hidden="true" />
              </template>
              {{ t('clientportal.events.detail.speakersFilters') }}
            </CPButton>
          </template>
          <div class="flex min-w-[14rem] flex-col gap-2 p-3">
            <div class="flex items-center justify-between gap-2">
              <CPText size="sm" weight="semibold" class="text-body">
                {{ t('clientportal.events.detail.speakersFilterByLabel') }}
              </CPText>
              <CPButton
                :id="createElementId(idPrefix, 'speakers', 'filters-clear')"
                type="text"
                color="secondary"
                size="xs"
                @click="clearLabelFilters"
              >
                {{ t('clientportal.events.detail.speakersFilterClear') }}
              </CPButton>
            </div>
            <div :id="createElementId(idPrefix, 'speakers', 'filters-options')" class="flex max-h-60 flex-col gap-2 overflow-y-auto">
              <CPCheckbox
                v-for="label in uniqueLabels"
                :id="labelFilterId(label)"
                :key="label"
                :label="label"
                :checked="isLabelSelected(label)"
                size="sm"
                @update:checked="checked => setLabelSelected(label, checked)"
              />
            </div>
          </div>
        </CPPopover>
      </div>
    </div>

    <div class="grid grid-cols-[repeat(auto-fill,minmax(16.125rem,1fr))] gap-5">
      <div
        v-for="speaker in matchedSpeakers"
        :id="createElementId(idPrefix, 'speaker', speaker.id)"
        :key="speaker.id"
        role="button"
        tabindex="0"
        class="group flex cursor-pointer flex-col gap-3.5"
        :aria-label="t('clientportal.events.detail.speakersOpenDetails', { name: speaker.name })"
        @click="openSpeaker(speaker)"
        @keydown.enter.prevent="openSpeaker(speaker)"
        @keydown.space.prevent="openSpeaker(speaker)"
      >
        <div
          :id="createElementId(idPrefix, 'speaker-photo-wrap', speaker.id)"
          class="aspect-square w-full overflow-hidden rounded-2xl bg-[var(--cp-primary-100)]"
        >
          <OptimisedImage
            v-if="speaker.imageUrl"
            :id="createElementId(idPrefix, 'speaker-photo', speaker.id)"
            :src="speaker.imageUrl"
            :alt="speaker.name"
            source="client-portal"
            :use-raw-url="true"
            class="h-full w-full"
            img-class="h-full w-full object-cover object-[center_8%] transition-transform duration-[140ms] ease-out group-hover:scale-[1.03]"
            fallback-aspect="aspect-square"
          />
          <div v-else class="flex h-full w-full items-center justify-center">
            <CPAvatar :title="speaker.name" size="2xl" shape="square" source="client-portal" class="shrink-0 w-full h-full" />
          </div>
        </div>
        <div class="flex min-w-0 flex-col gap-1">
          <CPTag
            v-if="speaker.tag"
            :id="createElementId(idPrefix, 'speaker-tag', speaker.id)"
            size="xs"
            color="purple"
            :round="true"
            :bordered="false"
            :interactive="false"
            class="self-start"
          >
            <CPText size="xs" weight="bold" class="uppercase tracking-[0.12em]">{{ speaker.tag }}</CPText>
          </CPTag>
          <CPText
            :id="createElementId(idPrefix, 'speaker-name', speaker.id)"
            size="2xl"
            weight="semibold"
            html-tag="div"
            class="text-pretty tracking-[-0.02em] text-body"
          >
            {{ speaker.name }}
          </CPText>
          <CPText v-if="speaker.role" size="xs" weight="bold" html-tag="div" class="uppercase tracking-[0.06em] text-700">
            {{ speaker.role }}
          </CPText>
          <CPText
            v-if="speaker.bio"
            :id="createElementId(idPrefix, 'speaker-bio', speaker.id)"
            size="md"
            weight="normal"
            html-tag="div"
            class="speaker-bio-clamp text-pretty text-500"
          >
            {{ speaker.bio }}
          </CPText>
        </div>
      </div>
    </div>

    <CPText v-if="matchedSpeakers.length === 0" size="md" weight="normal" class="pb-8 text-center text-500">
      {{ t('clientportal.events.detail.speakersEmpty') }}
    </CPText>
  </div>
</template>

<style scoped>
  .speaker-bio-clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    overflow: hidden;
  }
</style>
